import { useInfiniteQuery } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import ContentSpinner from '@/components/ContentSpinner';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, TeamHeading } from '@/components/Layout/Parts';
import useAlerts from '@/hooks/useAlerts';
import {
  NoSchedulesText,
  NoShcedules,
} from '@/pages/DistributionSchedule/components/DistributionList';
import DistributionListItem from '@/pages/DistributionSchedule/components/DistributionListItem';
import { PaginatedResponse, ReleaseResource } from '@/types/resources';
import { api } from '@/utils/api';

import LanguagesFilter from './components/LanguagesFilter';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 0 12px;
`;

const MLBacklog: FC = () => {
  const [languages, setLanguages] = useState<string[]>([]);
  const alerts = useAlerts();

  const fetchReleases = ({ pageParam = '/v1/releases' }) => {
    return api.get<never, PaginatedResponse<ReleaseResource>>(pageParam, {
      params: {
        per_page: 20,
        sort: '-createdAt',
        'filter[multilang]': 1,
        ...(!!languages.length && {
          'filter[language]': languages.join(','),
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'releases',
      {
        sort: '-createdAt',
        languages: languages.join(','),
        mlBacklog: true,
      },
    ],
    fetchReleases,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 90 * 1000,
    },
  );

  const releases = useMemo(
    () =>
      data?.pages?.reduce<ReleaseResource[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  useEffect(() => {
    if (!isError) {
      return;
    }

    alerts.error();
  }, [isError, alerts]);

  return (
    <>
      <TeamHeading />
      <PageHeading>Multi-language Distribution Backlog</PageHeading>
      <Header>
        <LanguagesFilter
          selectedLanguages={languages}
          setSelectedLanguages={setLanguages}
        />
      </Header>
      {!data && isLoading ? (
        <ContentSpinner />
      ) : (
        <>
          {!releases?.length ? (
            <NoShcedules>
              <NoSchedulesText $type="caption">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.4616 4.42857H5.53854C5.07122 4.42857 4.69238 4.81233 4.69238 5.28572V22.4286C4.69238 22.902 5.07122 23.2857 5.53854 23.2857H22.4616C22.9289 23.2857 23.3078 22.902 23.3078 22.4286V5.28572C23.3078 4.81233 22.9289 4.42857 22.4616 4.42857Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19.0767 2.71428V6.14285"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.92285 2.71428V6.14285"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.69238 9.57143H23.3078"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                No releases found
              </NoSchedulesText>
            </NoShcedules>
          ) : (
            <>
              <List>
                {releases.map((release) => (
                  <DistributionListItem key={release.id} release={release} />
                ))}
              </List>
              <InfiniteLoader
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default MLBacklog;
